import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { ThemeEmoji } from './ThemeEmoji';

export type BtnVariant =
  | 'primary'
  | 'accent'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'outline-accent';
export type BtnSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  isLoading?: boolean;
}

const sizes: Record<BtnSize, string> = {
  sm: 'text-[11.5px] px-2.5 py-1.5 gap-1.5',
  md: 'text-[12px] px-3 py-1.5 gap-1.5',
  lg: 'text-[12.5px] px-4 py-2 gap-2',
};

// Plan 41-14 Task 0b — accent + outline-accent use ms-orange-dark for text/bg
// to satisfy WCAG 2.1 AA 4.5:1 contrast:
//   - accent: white-on-#A0521D = 5.65:1 (was white-on-#D2691E = 3.63:1)
//   - outline-accent: text-ms-orange-dark on bg-ms-orange/5 = ~5.0:1 (was 3.06:1)
// Visual identity preserved — same orange family, slightly darker shade.
// MKS-493 — `accent` swapped its literal `text-white` for `--ms-on-accent`
// (default `#FFFFFF`, so unchanged here) and gained an inset hairline from
// `--ms-accent-border` (default `transparent`, so invisible here). Both are
// what lets Sparkle render the #f4a7c0 / #4a1d32 / #e88aab primary button
// without white-on-pink at 2:1 — and the hairline is an INSET SHADOW, not a
// border, so turning it on cannot move a single pixel of layout.
const variants: Record<BtnVariant, string> = {
  primary: 'bg-[#1D1D25] text-white hover:bg-[#2E2E38]',
  accent:
    'bg-ms-orange-dark text-[color:var(--ms-on-accent)] shadow-[inset_0_0_0_1px_var(--ms-accent-border)] hover:opacity-90',
  secondary:
    'bg-white border border-ms-border text-ms-text-secondary hover:bg-ms-surface-2',
  ghost: 'text-ms-text-secondary hover:bg-ms-surface-2',
  danger: 'bg-rose-600 text-white hover:bg-rose-700',
  'outline-accent':
    'border border-ms-orange/40 bg-ms-orange/5 text-ms-orange-dark hover:bg-ms-orange/10',
};

// `rounded-ms-button` resolves to `var(--ms-radius-button)`, whose default is
// 0.375rem — exactly the `rounded-md` it replaces.
const base =
  'ms-btn inline-flex items-center justify-center font-medium rounded-ms-button transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-ms-orange/40 disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * Btn — v7 multi-variant button primitive.
 *
 * Mirrors the `forwardRef` + `variants/sizes` record skeleton from
 * `frontend/src/components/common/Button.tsx`; replaces the variant string
 * values with shell.jsx tokens (PATTERNS.md lines 124-134) and adds the
 * v7-specific `accent` and `outline-accent` variants. Co-exists with the
 * legacy Button — Wave 2 surfaces opt-in.
 */
export const Btn = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'accent', size = 'md', isLoading, className = '', children, disabled, ...props },
    ref
  ) => (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={`${base} ms-btn-${variant} ${sizes[size]} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
      {/* MKS-493 — the main-action emoji, Sparkle only, on the two variants
          that ARE the main action (`accent` is the default, so this is the
          "+ Log hours" / "Save" button everywhere). Renders nothing in the
          default theme, and is aria-hidden, so the button's accessible name
          is unchanged in both. */}
      {(variant === 'accent' || variant === 'primary') && <ThemeEmoji role="action" />}
    </button>
  )
);
Btn.displayName = 'Btn';
